import { Box, Button, Typography } from '@mui/material';
import type { CredentialCheck } from '../../api/admin.api';
import { cardSurfaceSx } from '../../theme';

type Props = {
  check: CredentialCheck | undefined;
  onContinue: () => void;
};

const WHERE = { all: 'all repositories', selected: 'the repositories chosen' } as const;

/**
 * What GitHub made of an app the moment its key was stored. **A refusal is not drawn
 * here**: the key is saved but wrong, so the form stays open to correct it, and the
 * message sits beside the form. This is for the two outcomes that end the task.
 */
export const AppConnectionResult = ({ check, onContinue }: Props) => (
  <Box role="status" sx={{ ...cardSurfaceSx, padding: 6, display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 720 }}>
    {check?.state === 'verified' ? (
      <>
        <Typography variant="h3" component="h2">
          Connected as {check.name}
        </Typography>
        {check.installations.length === 0 ? (
          <Typography variant="body2">
            GitHub recognises the app, but it is not installed anywhere yet. Install it on the organisation and choose
            the repositories to read, or no repository can be pulled in.
          </Typography>
        ) : (
          <Box component="ul" sx={{ margin: 0, paddingLeft: 6 }}>
            {check.installations.map((installation) => (
              <Typography component="li" variant="body2" key={installation.account}>
                Installed on {installation.account}, with access to {WHERE[installation.repositorySelection]}.
              </Typography>
            ))}
          </Box>
        )}
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          A repository can only be read once it is one of those the installation covers.
        </Typography>
      </>
    ) : (
      <>
        <Typography variant="h3" component="h2">
          Saved, but not checked
        </Typography>
        <Typography variant="body2">
          The key is stored. GitHub could not be asked about it just now, so the first repository you add will be
          the test.
        </Typography>
      </>
    )}
    <Box>
      <Button variant="contained" onClick={onContinue}>
        Continue
      </Button>
    </Box>
  </Box>
);
